/**
 * Walks the AI chat's `UIMessage[]` tape and yields the structured
 * artefacts the server's tools produce — invoice plans (approval-
 * gated), drafted reminders, dashboard snapshots. Mirrors
 * `apps/mobile/features/ai/extractPlan.ts` so both surfaces render
 * the same things from the same parts; web just doesn't take a
 * dependency on `@ai-sdk/react`.
 *
 * Anchoring: each artefact remembers the `messageIndex` of the
 * assistant turn that produced it, so the chat surface can render
 * the card inline at its turn position instead of pinning a stale
 * card to the bottom of the scroll view.
 *
 * `createInvoice` lifecycle: the Vercel AI SDK names mutating-tool
 * parts `tool-<toolName>` and cycles `state` through
 *   `input-streaming` → `input-available`
 *   → `approval-requested` (we render PlanCard with Accept)
 *   → `approval-responded` (user tapped Accept; SDK auto-resubmits)
 *   → `output-available` (tool executed; we flip PlanCard to the
 *      "Created INV-NNN" badge using `output.data.invoiceNumber`).
 * The walker classifies plans by that final state.
 */
import type { InvoicePlanV1 } from '@repo/api-client/dtos';

/**
 * Local UIMessage shim — structurally compatible with `@ai-sdk/react`'s
 * `UIMessage`. We accept `unknown` parts and narrow with cheap type
 * guards so a future SDK rename of a part field stops surfacing the
 * artefact instead of crashing the renderer.
 */
export interface UiMessage {
  id?: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  parts?: unknown[];
}

/**
 * The invoice-plan input the model sent to `createInvoice`. Lives on
 * the `tool-createInvoice` part as `part.input`. The PlanCard renders
 * the line items + client + dates straight from here while the
 * approval is pending; the server-side `execute` runs only after
 * approval and the resulting `output.data.plan` carries
 * server-computed totals.
 */
export interface CreateInvoiceInput {
  clientId: string;
  clientName: string;
  issueDate: string;
  dueDate?: string;
  lineItems: Array<{
    description: string;
    quantity: number;
    unit: string;
    unitPrice: string;
    taxable: boolean;
  }>;
  notes?: string;
  targetInvoiceId?: string;
  targetInvoiceNumber?: string;
}

export interface ActivePlan {
  /**
   * The SDK's `approval.id` — pass this to
   * `chat.addToolApprovalResponse({ id, approved })` when the user
   * taps Accept. Only present while `state === 'approval-requested'`.
   */
  approvalId: string | null;
  toolCallId: string;
  input: CreateInvoiceInput;
  /**
   * Set once the SDK has run `createInvoice.execute` and emitted the
   * resolved output. Drives the PlanCard's "Created INV-NNN" badge.
   */
  resolved: { invoiceId: string; invoiceNumber: string; plan: InvoicePlanV1 } | null;
  messageIndex: number;
}

export function extractAllPlans(messages: readonly UiMessage[]): ActivePlan[] {
  const out: ActivePlan[] = [];
  for (let i = 0; i < messages.length; i += 1) {
    const msg = messages[i];
    if (!msg) continue;
    const parts = msg.parts;
    if (!Array.isArray(parts)) continue;
    // Take the LAST createInvoice tool part per message. If the model
    // retries within one turn, the latest call reflects the final
    // intent; older retries shouldn't render their own card.
    let picked: ActivePlan | null = null;
    for (const raw of parts) {
      const part = raw as Record<string, unknown> | null | undefined;
      if (!part) continue;
      const candidate = readCreateInvoicePart(part, i);
      if (candidate) picked = candidate;
    }
    if (picked) out.push(picked);
  }
  return out;
}

export function extractLatestPlan(
  messages: readonly UiMessage[],
): ActivePlan | null {
  const all = extractAllPlans(messages);
  return all.length > 0 ? all[all.length - 1]! : null;
}

function readCreateInvoicePart(
  part: Record<string, unknown>,
  messageIndex: number,
): ActivePlan | null {
  if (part.type !== 'tool-createInvoice') return null;
  const state = typeof part.state === 'string' ? part.state : null;
  // Only states that have an input shape ready for rendering surface
  // as a PlanCard. The `input-streaming` state has a partial input
  // (DeepPartial) that's noisy to render — wait for `input-available`
  // and beyond.
  if (
    state !== 'input-available' &&
    state !== 'approval-requested' &&
    state !== 'approval-responded' &&
    state !== 'output-available' &&
    state !== 'output-denied' &&
    state !== 'output-error'
  ) {
    return null;
  }
  const input = part.input as Partial<CreateInvoiceInput> | undefined;
  if (
    !input ||
    typeof input.clientId !== 'string' ||
    !Array.isArray(input.lineItems) ||
    input.lineItems.length === 0
  ) {
    return null;
  }
  const toolCallId =
    typeof part.toolCallId === 'string' ? part.toolCallId : '';
  const approval = part.approval as
    | { id?: unknown; approved?: unknown }
    | undefined;
  const approvalId =
    state === 'approval-requested' &&
    approval &&
    typeof approval.id === 'string'
      ? approval.id
      : null;
  let resolved: ActivePlan['resolved'] = null;
  if (state === 'output-available') {
    const output = part.output as { ok?: boolean; data?: unknown } | undefined;
    if (output && output.ok !== false) {
      const data = output.data as
        | {
            invoiceId?: unknown;
            invoiceNumber?: unknown;
            plan?: unknown;
          }
        | undefined;
      if (
        data &&
        typeof data.invoiceId === 'string' &&
        typeof data.invoiceNumber === 'string'
      ) {
        resolved = {
          invoiceId: data.invoiceId,
          invoiceNumber: data.invoiceNumber,
          plan: data.plan as InvoicePlanV1,
        };
      }
    }
  }
  return {
    approvalId,
    toolCallId,
    input: input as CreateInvoiceInput,
    resolved,
    messageIndex,
  };
}

// ---------------------------------------------------------------------------
// draftReminder

export type ReminderTone = 'friendly' | 'firm' | 'final';

export interface ReminderArtefact {
  invoiceId: string;
  invoiceNumber: string;
  tone: ReminderTone;
  daysOverdue: number;
  amountDue: string;
  amountDueFormatted: string;
  currency: string;
  client: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
  };
  org: { name: string };
  subject: string;
  body: string;
}

export interface ActiveReminder {
  artefact: ReminderArtefact;
  messageIndex: number;
}

export function extractAllReminders(
  messages: readonly UiMessage[],
): ActiveReminder[] {
  const out: ActiveReminder[] = [];
  for (let i = 0; i < messages.length; i += 1) {
    const msg = messages[i];
    if (!msg) continue;
    const parts = msg.parts;
    if (!Array.isArray(parts)) continue;
    let picked: ReminderArtefact | null = null;
    for (const raw of parts) {
      const part = raw as Record<string, unknown> | null | undefined;
      if (!part) continue;
      const candidate = readDraftReminderPart(part);
      if (candidate) picked = candidate;
    }
    if (picked) out.push({ artefact: picked, messageIndex: i });
  }
  return out;
}

function readDraftReminderPart(
  part: Record<string, unknown>,
): ReminderArtefact | null {
  if (part.type !== 'tool-draftReminder') return null;
  if (typeof part.state === 'string' && part.state !== 'output-available') {
    return null;
  }
  const output = part.output as { ok?: boolean; data?: unknown } | undefined;
  if (!output || output.ok === false) return null;
  const data = output.data as Partial<ReminderArtefact> | undefined;
  if (
    !data ||
    typeof data.subject !== 'string' ||
    typeof data.body !== 'string' ||
    typeof data.invoiceId !== 'string' ||
    typeof data.invoiceNumber !== 'string'
  ) {
    return null;
  }
  return data as ReminderArtefact;
}

// ---------------------------------------------------------------------------
// getDashboardStats

export interface StatsTile {
  amount: string;
  amountFormatted: string;
  count: number;
  currency: string;
}

export interface StatsArtefact {
  period: 'week' | 'month' | 'quarter' | 'year';
  periodLabel: string;
  currency: string;
  outstanding: StatsTile;
  overdue: StatsTile & { oldestDays: number | null };
  paidInPeriod: StatsTile;
  drafts: StatsTile;
  topOutstandingClient: {
    name: string;
    amount: string;
    amountFormatted: string;
    invoiceCount: number;
  } | null;
}

export interface ActiveStats {
  artefact: StatsArtefact;
  messageIndex: number;
}

export function extractAllStats(messages: readonly UiMessage[]): ActiveStats[] {
  const out: ActiveStats[] = [];
  for (let i = 0; i < messages.length; i += 1) {
    const msg = messages[i];
    if (!msg) continue;
    const parts = msg.parts;
    if (!Array.isArray(parts)) continue;
    let picked: StatsArtefact | null = null;
    for (const raw of parts) {
      const part = raw as Record<string, unknown> | null | undefined;
      if (!part) continue;
      const candidate = readDashboardStatsPart(part);
      if (candidate) picked = candidate;
    }
    if (picked) out.push({ artefact: picked, messageIndex: i });
  }
  return out;
}

function readDashboardStatsPart(
  part: Record<string, unknown>,
): StatsArtefact | null {
  if (part.type !== 'tool-getDashboardStats') return null;
  if (typeof part.state === 'string' && part.state !== 'output-available') {
    return null;
  }
  const output = part.output as { ok?: boolean; data?: unknown } | undefined;
  if (!output || output.ok === false) return null;
  const data = output.data as Partial<StatsArtefact> | undefined;
  if (
    !data ||
    typeof data.periodLabel !== 'string' ||
    typeof data.outstanding !== 'object' ||
    typeof data.overdue !== 'object' ||
    typeof data.paidInPeriod !== 'object' ||
    typeof data.drafts !== 'object'
  ) {
    return null;
  }
  return data as StatsArtefact;
}

// ---------------------------------------------------------------------------
// Text extraction

export function extractMessageText(message: UiMessage): string {
  const parts = message.parts;
  if (!Array.isArray(parts)) return '';
  let out = '';
  for (const raw of parts) {
    const p = raw as { type?: string; text?: unknown } | null | undefined;
    if (p && p.type === 'text' && typeof p.text === 'string') {
      out += p.text;
    }
  }
  return out;
}
